/**
 * FikoSPHSensorGrafik - Sensör Veri Toplama ve Grafik Çizimi
 *
 * Sensör okumalarını toplar, Canvas 2D API ile gerçek zamanlı
 * grafikler çizer ve veri dışa aktarma sağlar.
 *
 * @author FikoYT CAD System
 * @version 1.0.0
 */

export interface GrafikVeri {
  sensorId: string;
  sensorAd: string;
  sensorTipi: number;
  veriNoktlari: { zaman: number; deger: number }[];
  renk: { r: number; g: number; b: number };
}

export interface GrafikAyarlari {
  genislik: number;           // canvas width
  yukseklik: number;          // canvas height
  zamanAraligi: number;       // visible time window (seconds)
  otomatikOlcek: boolean;     // auto-scale Y axis
  minY: number;
  maxY: number;
  gridGoster: boolean;
  etiketGoster: boolean;
  arkaPlanRenk: string;
  gridRenk: string;
  yaziRenk: string;
  kenarPadding: number;
}

const VARSAYILAN_RENKLER = [
  { r: 255, g: 99, b: 132 },   // Kırmızı
  { r: 54, g: 162, b: 235 },   // Mavi
  { r: 75, g: 192, b: 192 },   // Turkuaz
  { r: 255, g: 206, b: 86 },   // Sarı
  { r: 153, g: 102, b: 255 },  // Mor
  { r: 255, g: 159, b: 64 },   // Turuncu
  { r: 46, g: 204, b: 113 },   // Yeşil
  { r: 231, g: 76, b: 60 }     // Koyu kırmızı
];

export class FikoSPHSensorGrafik {
  private grafikler: Map<string, GrafikVeri>;
  private ayarlar: GrafikAyarlari;
  private canvas: HTMLCanvasElement | null;
  private ctx: CanvasRenderingContext2D | null;
  private renkIndeksi: number;
  private maxVeriNoktasi: number;

  constructor(ayarlar?: Partial<GrafikAyarlari>) {
    this.grafikler = new Map();
    this.canvas = null;
    this.ctx = null;
    this.renkIndeksi = 0;
    this.maxVeriNoktasi = 1000; // Her sensör için max nokta sayısı

    this.ayarlar = {
      genislik: 800,
      yukseklik: 400,
      zamanAraligi: 30, // 30 saniye
      otomatikOlcek: true,
      minY: 0,
      maxY: 100,
      gridGoster: true,
      etiketGoster: true,
      arkaPlanRenk: '#1a1a1a',
      gridRenk: '#333333',
      yaziRenk: '#ffffff',
      kenarPadding: 50,
      ...ayarlar
    };
  }

  /**
   * Canvas elementine bağlan
   */
  baglaCanvas(canvas: HTMLCanvasElement): void {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d');

    if (this.ctx) {
      // Canvas boyutunu ayarla
      canvas.width = this.ayarlar.genislik;
      canvas.height = this.ayarlar.yukseklik;

      // High DPI desteği
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();

      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;

      this.ctx.scale(dpr, dpr);

      // Ayarları güncelle
      this.ayarlar.genislik = rect.width;
      this.ayarlar.yukseklik = rect.height;
    }
  }

  /**
   * Yeni sensör ekle
   */
  sensorEkle(
    sensorId: string,
    ad: string,
    tipi: number,
    renk?: { r: number, g: number, b: number }
  ): void {
    if (this.grafikler.has(sensorId)) {
      console.warn(`Sensör zaten mevcut: ${sensorId}`);
      return;
    }

    // Renk belirtilmemişse otomatik ata
    const sensorRenk = renk || VARSAYILAN_RENKLER[this.renkIndeksi % VARSAYILAN_RENKLER.length];
    this.renkIndeksi++;

    this.grafikler.set(sensorId, {
      sensorId,
      sensorAd: ad,
      sensorTipi: tipi,
      veriNoktlari: [],
      renk: sensorRenk
    });
  }

  /**
   * Sensörü sil
   */
  sensorSil(sensorId: string): void {
    this.grafikler.delete(sensorId);
  }

  /**
   * Sensöre veri noktası ekle
   */
  veriEkle(sensorId: string, zaman: number, deger: number): void {
    const grafik = this.grafikler.get(sensorId);
    if (!grafik) {
      console.warn(`Sensör bulunamadı: ${sensorId}`);
      return;
    }

    grafik.veriNoktlari.push({ zaman, deger });

    // Eski noktaları temizle
    const minZaman = zaman - this.ayarlar.zamanAraligi * 2; // 2x buffer
    grafik.veriNoktlari = grafik.veriNoktlari.filter(p => p.zaman >= minZaman);

    // Max nokta sınırı
    if (grafik.veriNoktlari.length > this.maxVeriNoktasi) {
      grafik.veriNoktlari.shift();
    }
  }

  /**
   * Tüm grafikleri çiz
   */
  ciz(): void {
    if (!this.ctx || !this.canvas) {
      return;
    }

    const ctx = this.ctx;
    const { genislik, yukseklik, arkaPlanRenk } = this.ayarlar;

    // Temizle
    ctx.fillStyle = arkaPlanRenk;
    ctx.fillRect(0, 0, genislik, yukseklik);

    // Grid çiz
    if (this.ayarlar.gridGoster) {
      this.cizGrid();
    }

    // Ölçeği hesapla
    const olcek = this.hesaplaOlcek();

    // Her sensörün grafiğini çiz
    this.grafikler.forEach(grafik => {
      this.cizCizgi(grafik, olcek);
    });

    // Etiketleri çiz
    if (this.ayarlar.etiketGoster) {
      this.cizEtiketler(olcek);
    }

    // Legend çiz
    this.cizLegend();
  }

  /**
   * Grid çiz
   */
  private cizGrid(): void {
    if (!this.ctx) return;

    const ctx = this.ctx;
    const { genislik, yukseklik, kenarPadding, gridRenk } = this.ayarlar;

    ctx.strokeStyle = gridRenk;
    ctx.lineWidth = 1;

    // Dikey grid çizgileri (zaman)
    const gridSayisi = 10;
    for (let i = 0; i <= gridSayisi; i++) {
      const x = kenarPadding + ((genislik - 2 * kenarPadding) / gridSayisi) * i;
      ctx.beginPath();
      ctx.moveTo(x, kenarPadding);
      ctx.lineTo(x, yukseklik - kenarPadding);
      ctx.stroke();
    }

    // Yatay grid çizgileri (değer)
    for (let i = 0; i <= gridSayisi; i++) {
      const y = kenarPadding + ((yukseklik - 2 * kenarPadding) / gridSayisi) * i;
      ctx.beginPath();
      ctx.moveTo(kenarPadding, y);
      ctx.lineTo(genislik - kenarPadding, y);
      ctx.stroke();
    }

    // Eksen çerçevesi
    ctx.strokeStyle = this.ayarlar.yaziRenk;
    ctx.lineWidth = 2;
    ctx.strokeRect(
      kenarPadding,
      kenarPadding,
      genislik - 2 * kenarPadding,
      yukseklik - 2 * kenarPadding
    );
  }

  /**
   * Çizgi grafiği çiz
   */
  private cizCizgi(grafik: GrafikVeri, olcek: { minY: number; maxY: number }): void {
    if (!this.ctx || grafik.veriNoktlari.length < 2) return;

    const ctx = this.ctx;
    const { genislik, yukseklik, kenarPadding, zamanAraligi } = this.ayarlar;

    // En son zaman noktası
    const sonZaman = Math.max(...grafik.veriNoktlari.map(p => p.zaman));
    const baslangicZaman = sonZaman - zamanAraligi;

    // Görünür noktaları filtrele
    const gorunurNoktlar = grafik.veriNoktlari.filter(p => p.zaman >= baslangicZaman);
    if (gorunurNoktlar.length < 2) return;

    // Renk
    const { r, g, b } = grafik.renk;
    ctx.strokeStyle = `rgb(${r}, ${g}, ${b})`;
    ctx.lineWidth = 2;
    ctx.lineJoin = 'round';

    // Çizim alanı boyutları
    const cizimGenislik = genislik - 2 * kenarPadding;
    const cizimYukseklik = yukseklik - 2 * kenarPadding;
    const deltaY = olcek.maxY - olcek.minY;

    ctx.beginPath();

    gorunurNoktlar.forEach((nokta, index) => {
      // X pozisyonu (zaman)
      const normalizeX = (nokta.zaman - baslangicZaman) / zamanAraligi;
      const x = kenarPadding + normalizeX * cizimGenislik;

      // Y pozisyonu (değer) - ters çevir (canvas Y ekseni yukarıdan aşağıya)
      const normalizeY = deltaY > 0 ? (nokta.deger - olcek.minY) / deltaY : 0.5;
      const y = yukseklik - kenarPadding - normalizeY * cizimYukseklik;

      if (index === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    });

    ctx.stroke();

    // Son nokta işaretleyici
    if (gorunurNoktlar.length > 0) {
      const sonNokta = gorunurNoktlar[gorunurNoktlar.length - 1];
      const normalizeX = (sonNokta.zaman - baslangicZaman) / zamanAraligi;
      const x = kenarPadding + normalizeX * cizimGenislik;
      const normalizeY = deltaY > 0 ? (sonNokta.deger - olcek.minY) / deltaY : 0.5;
      const y = yukseklik - kenarPadding - normalizeY * cizimYukseklik;

      ctx.fillStyle = `rgb(${r}, ${g}, ${b})`;
      ctx.beginPath();
      ctx.arc(x, y, 4, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  /**
   * Eksen etiketlerini çiz
   */
  private cizEtiketler(olcek: { minY: number; maxY: number }): void {
    if (!this.ctx) return;

    const ctx = this.ctx;
    const { genislik, yukseklik, kenarPadding, yaziRenk, zamanAraligi } = this.ayarlar;

    ctx.fillStyle = yaziRenk;
    ctx.font = '12px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';

    // X ekseni (zaman) etiketleri
    const zamanAdim = 5;
    const zamanEtiketSayisi = Math.floor(zamanAraligi / zamanAdim);

    for (let i = 0; i <= zamanEtiketSayisi; i++) {
      const zaman = i * zamanAdim;
      const x = kenarPadding + ((genislik - 2 * kenarPadding) / zamanEtiketSayisi) * i;
      const etiket = `-${(zamanAraligi - zaman).toFixed(0)}s`;
      ctx.fillText(etiket, x, yukseklik - kenarPadding + 5);
    }

    // Y ekseni (değer) etiketleri
    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';

    const degerAdimSayisi = 5;
    for (let i = 0; i <= degerAdimSayisi; i++) {
      const normalize = i / degerAdimSayisi;
      const deger = olcek.minY + (olcek.maxY - olcek.minY) * normalize;
      const y = yukseklik - kenarPadding - normalize * (yukseklik - 2 * kenarPadding);
      ctx.fillText(deger.toFixed(2), kenarPadding - 10, y);
    }
  }

  /**
   * Legend çiz (sağ üst köşe)
   */
  private cizLegend(): void {
    if (!this.ctx || this.grafikler.size === 0) return;

    const ctx = this.ctx;
    const { genislik, kenarPadding, yaziRenk } = this.ayarlar;

    const legendX = genislik - kenarPadding - 150;
    let legendY = kenarPadding + 10;

    ctx.font = 'bold 12px Arial';
    ctx.fillStyle = yaziRenk;
    ctx.textAlign = 'left';
    ctx.fillText('Sensörler:', legendX, legendY);
    legendY += 20;

    ctx.font = '11px Arial';

    this.grafikler.forEach(grafik => {
      const { r, g, b } = grafik.renk;

      // Renk kutusu
      ctx.fillStyle = `rgb(${r}, ${g}, ${b})`;
      ctx.fillRect(legendX, legendY - 6, 12, 12);

      // Sensör adı
      ctx.fillStyle = yaziRenk;
      ctx.fillText(grafik.sensorAd, legendX + 18, legendY);

      // Son değer
      if (grafik.veriNoktlari.length > 0) {
        const sonDeger = grafik.veriNoktlari[grafik.veriNoktlari.length - 1].deger;
        ctx.fillText(`: ${sonDeger.toFixed(2)}`, legendX + 18 + ctx.measureText(grafik.sensorAd).width + 5, legendY);
      }

      legendY += 18;
    });
  }

  /**
   * Y ekseni ölçeğini hesapla
   */
  private hesaplaOlcek(): { minY: number; maxY: number } {
    if (!this.ayarlar.otomatikOlcek) {
      return {
        minY: this.ayarlar.minY,
        maxY: this.ayarlar.maxY
      };
    }

    let minDeger = Number.MAX_VALUE;
    let maxDeger = -Number.MAX_VALUE;

    this.grafikler.forEach(grafik => {
      grafik.veriNoktlari.forEach(nokta => {
        minDeger = Math.min(minDeger, nokta.deger);
        maxDeger = Math.max(maxDeger, nokta.deger);
      });
    });

    if (minDeger === Number.MAX_VALUE) {
      return { minY: 0, maxY: 100 };
    }

    // Biraz margin ekle
    const delta = maxDeger - minDeger;
    const margin = delta * 0.1;

    return {
      minY: minDeger - margin,
      maxY: maxDeger + margin
    };
  }

  /**
   * PNG olarak dışa aktar
   */
  exportPNG(): string {
    if (!this.canvas) {
      return '';
    }
    return this.canvas.toDataURL('image/png');
  }

  /**
   * CSV olarak dışa aktar
   */
  exportCSV(sensorId?: string): string {
    const grafikler = sensorId
      ? [this.grafikler.get(sensorId)].filter(Boolean) as GrafikVeri[]
      : Array.from(this.grafikler.values());

    if (grafikler.length === 0) {
      return '';
    }

    // Header
    const headers = ['Zaman(s)', ...grafikler.map(g => g.sensorAd)];
    const rows: string[] = [headers.join(',')];

    // Tüm zaman noktalarını topla
    const zamanSet = new Set<number>();
    grafikler.forEach(g => {
      g.veriNoktlari.forEach(p => zamanSet.add(p.zaman));
    });

    const zamanlar = Array.from(zamanSet).sort((a, b) => a - b);

    // Her zaman noktası için satır oluştur
    zamanlar.forEach(zaman => {
      const satir = [zaman.toFixed(3)];

      grafikler.forEach(grafik => {
        const nokta = grafik.veriNoktlari.find(p => Math.abs(p.zaman - zaman) < 0.001);
        satir.push(nokta ? nokta.deger.toFixed(4) : '');
      });

      rows.push(satir.join(','));
    });

    return rows.join('\n');
  }

  /**
   * JSON olarak dışa aktar
   */
  exportJSON(): string {
    const data: Record<string, any> = {};

    this.grafikler.forEach((grafik, id) => {
      data[id] = {
        ad: grafik.sensorAd,
        tipi: grafik.sensorTipi,
        renk: grafik.renk,
        veri: grafik.veriNoktlari
      };
    });

    return JSON.stringify(data, null, 2);
  }

  /**
   * Tüm verileri ve sensörleri temizle
   */
  temizle(): void {
    this.grafikler.clear();
    this.renkIndeksi = 0;
    this.canvas = null;
    this.ctx = null;
  }

  /**
   * Tüm sensör verilerini temizle
   */
  veriTemizle(): void {
    this.grafikler.forEach(grafik => {
      grafik.veriNoktlari = [];
    });
  }

  /**
   * Belirli bir sensörün verilerini temizle
   */
  sensorVeriTemizle(sensorId: string): void {
    const grafik = this.grafikler.get(sensorId);
    if (grafik) {
      grafik.veriNoktlari = [];
    }
  }

  /**
   * Ayarları güncelle
   */
  setAyarlar(ayarlar: Partial<GrafikAyarlari>): void {
    this.ayarlar = { ...this.ayarlar, ...ayarlar };

    // Canvas boyutu değiştiyse yeniden ayarla
    if (this.canvas && (ayarlar.genislik || ayarlar.yukseklik)) {
      this.canvas.width = this.ayarlar.genislik;
      this.canvas.height = this.ayarlar.yukseklik;
    }
  }

  /**
   * Ayarları al
   */
  getAyarlar(): GrafikAyarlari {
    return { ...this.ayarlar };
  }

  /**
   * Sensör listesini al
   */
  getSensorler(): GrafikVeri[] {
    return Array.from(this.grafikler.values());
  }

  /**
   * Belirli bir sensörü al
   */
  getSensor(sensorId: string): GrafikVeri | undefined {
    const grafik = this.grafikler.get(sensorId);
    return grafik ? { ...grafik, veriNoktlari: [...grafik.veriNoktlari] } : undefined;
  }

  /**
   * Tüm sensör verilerini sıfırla (grafikleri temizle ama sensörleri koru)
   */
  sifirla(): void {
    for (const grafik of this.grafikler.values()) {
      grafik.veriNoktlari = [];
    }
  }
}
